import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useHttp } from 'src/hooks';
import { AuthContext } from './auth-context';

// Define types for the audience data
export interface AudienceTargeting {
    age_max: number;
    age_min: number;
    genders: number[];
    geo_locations: {
        countries: string[];
        location_types: string[];
    };
    interests?: Array<{
        id: string;
        name: string;
    }>;
}

export interface AudienceRawData {
    adSet: {
        id: string;
        name: string;
        targeting: AudienceTargeting;
        status: string;
        daily_budget: string;
        lifetime_budget: string;
    };
    insights: {
        impressions: number;
        spend: number;
        clicks: number;
        actions: any[];
        action_values: any[];
        cost_per_action_type: any[];
    };
}

export interface Audience {
    id: string;
    adAccountId: string;
    audience: string;
    location: string;
    date: string;
    group: string;
    status: string;
    cpm: string;
    cpc: string;
    leads: string;
    purchases: string;
    hook: string;
    hold: string;
    targeting: AudienceTargeting;
    rawData: AudienceRawData;
}

interface AudienceContextType {
    audiences: Audience[];
    isLoading: boolean;
    error: string | null;
    fetchAudiences: () => Promise<void>;
    totalCount: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    itemsPerPage: number;
    setItemsPerPage: (perPage: number) => void;
}

const AudienceContext = createContext<AudienceContextType | undefined>(undefined);

interface AudienceProviderProps {
    children: ReactNode;
}

export const AudienceProvider: React.FC<AudienceProviderProps> = ({ children }) => {
    const [audiences, setAudiences] = useState<Audience[]>([]);

    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [totalCount, setTotalCount] = useState(0);

    const { sendRequest, isLoading } = useHttp();
    const { accessToken, profile } = useContext(AuthContext);

    const fetchAudiences = async () => {
        if (!accessToken) {
            setError('Authentication required');
            return;
        }

        setError(null);

        try {
            await sendRequest(
                {
                    url: 'audiences/insights',
                    method: 'GET',
                    accessToken,
                },
                (data: any) => {
                    if (data && Array.isArray(data)) {
                        setAudiences(data);
                        setTotalCount(data.length);
                    } else {
                        setError('Invalid data format received from server');
                    }
                }
            );
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred');
        }
    };

    const value: AudienceContextType = {
        audiences,
        isLoading,
        error,
        fetchAudiences,
        totalCount,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
    };



    useEffect(() => {
        if (accessToken) {
            fetchAudiences();
        }
    }, [accessToken, profile]);


    return (
        <AudienceContext.Provider value={value}>
            {children}
        </AudienceContext.Provider>
    );
};

export const useAudience = (): AudienceContextType => {
    const context = useContext(AudienceContext);
    if (context === undefined) {
        throw new Error('useAudience must be used within an AudienceProvider');
    }
    return context;
};